import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth, useTheme } from "@/App";
import { Mic, BarChart3, Phone, ArrowRight, Sun, Moon, Sparkles, Target, TrendingUp } from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // If already logged in, redirect to dashboard
  if (user) {
    navigate("/dashboard");
    return null;
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden" data-testid="landing-page">
      {/* Background effects */}
      <div className="absolute inset-0 bg-glow pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <img
            src={theme === "dark" ? "/logo-light.png" : "/logo-dark.png"}
            alt="Great Marketing LLC"
            className="h-10 w-auto"
          />
        </div>
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="landing-theme-toggle">
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          <Button onClick={handleLogin} className="bg-primary hover:bg-primary/90" data-testid="login-btn">
            Sign In with Google
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
              <Sparkles className="w-4 h-4" />
              Real-Time AI Voice Training
            </div>
            
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
              Master Your
              <span className="block text-primary">Sales Pitch</span>
            </h2>
            
            <p className="text-lg text-muted-foreground max-w-lg">
              Practice with AI clients in real-time voice conversations. 
              Get instant feedback, handle objections, and close more deals.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                onClick={handleLogin}
                className="bg-primary hover:bg-primary/90 text-lg px-8 shadow-lg hover:shadow-primary/25 transition-all"
                data-testid="hero-cta-btn"
              >
                Start Training <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Stats */}
            <div className="flex gap-8 pt-4">
              <div>
                <p className="font-heading text-3xl font-bold text-foreground">Real-Time</p>
                <p className="text-sm text-muted-foreground">Voice AI</p>
              </div>
              <div>
                <p className="font-heading text-3xl font-bold text-foreground">Instant</p>
                <p className="text-sm text-muted-foreground">Feedback</p>
              </div>
              <div>
                <p className="font-heading text-3xl font-bold text-foreground">Track</p>
                <p className="text-sm text-muted-foreground">Progress</p>
              </div>
            </div>
          </div>

          {/* Right - Feature Cards */}
          <div className="relative">
            {/* Main illustration card */}
            <div className="relative bg-card rounded-2xl border border-border p-8 shadow-xl">
              
              {/* Voice visualizer preview */}
              <div className="w-full aspect-square max-w-xs mx-auto mb-6 rounded-2xl bg-muted/30 flex items-center justify-center">
                <div className="w-32 h-32 rounded-full bg-gradient-primary flex items-center justify-center shadow-lg voice-active">
                  <div className="w-24 h-24 rounded-full bg-background flex items-center justify-center">
                    <div className="flex items-center gap-1 h-12">
                      {[...Array(8)].map((_, i) => (
                        <div
                          key={i}
                          className="w-1.5 rounded-full bg-primary animate-waveform"
                          style={{
                            height: `${30 + Math.random() * 70}%`,
                            animationDelay: `${i * 0.1}s`
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-center text-muted-foreground text-sm">
                Natural voice conversations with AI - no push-to-talk
              </p>
            </div>

            {/* Floating feature cards */}
            <div className="absolute -left-4 top-1/4 bg-card rounded-xl border border-border p-4 shadow-lg hidden lg:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Target className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="font-medium text-sm">Objection Handling</p>
                  <p className="text-xs text-muted-foreground">Real-time practice</p>
                </div>
              </div>
            </div>

            <div className="absolute -right-4 bottom-1/4 bg-card rounded-xl border border-border p-4 shadow-lg hidden lg:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="font-medium text-sm">Track Progress</p>
                  <p className="text-xs text-muted-foreground">Detailed analytics</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="grid md:grid-cols-3 gap-6 mt-24">
          <div className="bg-card rounded-2xl border border-border p-6 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <Phone className="w-6 h-6 text-primary" />
            </div>
            <h3 className="font-heading font-semibold text-lg mb-2">Real-Time Voice Calls</h3>
            <p className="text-muted-foreground text-sm">
              Have natural conversations with AI clients using WebRTC. 
              No typing - just speak and the AI responds instantly.
            </p>
          </div>

          <div className="bg-card rounded-2xl border border-border p-6 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <BarChart3 className="w-6 h-6 text-primary" />
            </div>
            <h3 className="font-heading font-semibold text-lg mb-2">Detailed Feedback</h3>
            <p className="text-muted-foreground text-sm">
              Get scored on rapport building, objection handling, closing techniques, 
              and more with specific improvement suggestions.
            </p>
          </div>

          <div className="bg-card rounded-2xl border border-border p-6 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <Mic className="w-6 h-6 text-primary" />
            </div>
            <h3 className="font-heading font-semibold text-lg mb-2">Call Analysis</h3>
            <p className="text-muted-foreground text-sm">
              Upload real sales call recordings for AI analysis. 
              Get transcription and feedback on your actual conversations.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-border py-6 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-sm text-muted-foreground">
          <p>© 2025 Great Marketing LLC. Sales Training Platform.</p>
          <p>Powered by OpenAI Realtime</p>
        </div>
      </footer>
    </div>
  );
}
